import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SearchPaletteContext } from '../hooks/useSearchPalette';
import { CommandPalette } from './CommandPalette';

interface Props {
  children: React.ReactNode;
}

/**
 * Provider global pentru command palette. Tot ce trebuie e împachetat la
 * nivelul aplicației (sub Router și sub LanguageContext/CompanyContext).
 *
 * Shortcut: `Ctrl+K` (Linux/Win) sau `Cmd+K` (macOS) deschide/închide modalul.
 * `/` simplu deschide tot modalul, dar doar dacă focus-ul nu e pe un input.
 */
export const SearchPaletteProvider: React.FC<Props> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  // Shortcut global.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const isModifier = e.metaKey || e.ctrlKey;
      if (isModifier && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        toggle();
        return;
      }
      // `/` deschide doar dacă nu suntem deja într-un câmp text.
      if (e.key === '/' && !isOpen) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName?.toLowerCase();
        const isText =
          tag === 'input' ||
          tag === 'textarea' ||
          target?.getAttribute('contenteditable') === 'true';
        if (!isText) {
          e.preventDefault();
          open();
        }
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [toggle, open, isOpen]);

  const value = useMemo(() => ({ isOpen, open, close, toggle }), [isOpen, open, close, toggle]);

  return (
    <SearchPaletteContext.Provider value={value}>
      {children}
      <CommandPalette />
    </SearchPaletteContext.Provider>
  );
};
